import { useAmelioTranslations } from './useAmelioTranslations';
import { useError } from './useError';

import type { TranslationKey } from '@/i18n-key';

import { showSuccessToast } from '@/plugins/Toast';

interface ReportedWrite {
    write: () => Promise<unknown>;
    success: TranslationKey;
    failure: TranslationKey;
    /** The reason a refusal gives the user to act on, when it gives one; `failure` is shown otherwise. */
    explainRefusal?: (error: unknown) => TranslationKey | null;
}

/**
 * Runs a write the user asked for and tells them how it went: a toast on success, the reported error on
 * failure. Resolves to whether the write went through, so a screen can close or stay open.
 */
export const useReportedWrite = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    return async ({ write, success, failure, explainRefusal }: ReportedWrite) => {
        try {
            await write();
            showSuccessToast({ message: t(success) });
            return true;
        } catch (error) {
            handleError({
                error,
                data: {
                    title: t('errors.genericTitle'),
                    message: t(explainRefusal?.(error) ?? failure),
                },
            });
            return false;
        }
    };
};
